import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  LinearProgress,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import getUserRole from "../../helpers/jwt-token-access/userRole";
import {
  deleteScimToken,
  getScimConfig,
  postScimToken,
  postSsoConfigTest,
} from "../../helpers/auth_aws_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import { ACCOUNT_DETAIL_FONT_SX } from "../../components/AccountDetailPolicy/accountDetailTypography";
import {
  clearSsoConfigMessages,
  fetchSsoConfig,
  saveSsoConfig,
} from "../../store/ssoConfig/ssoConfigSlice";

const PROVIDERS = [
  { value: "OKTA", displayName: "Okta" },
  { value: "ONELOGIN", displayName: "OneLogin" },
  { value: "AD", displayName: "AD", label: "Microsoft Entra ID" },
];

const PREFILL_DISPLAY_NAMES = PROVIDERS.map((provider) => provider.displayName);

function resolveProvider(providerType) {
  if (!providerType) return null;
  const normalized = String(providerType).trim().toUpperCase();
  return PROVIDERS.find((item) => item.value === normalized) || null;
}

const DOMAIN_PATTERN =
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

const EDITABLE_FIELDS = [
  "displayName",
  "targetEmailDomain",
  "idpMetadataUrl",
  "enabled",
  "configStatus",
];

const TEST_REQUIRED_MESSAGE =
  "Run Test Connection on this metadata URL before turning on Single Sign-On.";
const TEST_FAILED_FALLBACK = "Test Connection failed. Check the metadata URL and try again.";

const SCIM_NOT_CONFIGURED = "SCIM token is not configured.";
const SCIM_SAVE_FIRST_MESSAGE = "Save the Single Sign-On configuration to set up SCIM provisioning.";
const SCIM_LOAD_FALLBACK = "SCIM settings could not be loaded. Try again.";
const SCIM_ACTION_FALLBACK = "The SCIM token could not be updated. Try again.";
const SCIM_TOKEN_FALLBACK_MESSAGE = "Copy this token now. It will not be shown again.";

const BRAND_ORANGE = "#F98125";
const BRAND_ORANGE_HOVER = "#e8741c";

const FIELD_LABEL_SX = {
  display: "block",
  mb: 0.75,
  fontSize: "0.8125rem",
  fontWeight: 600,
  lineHeight: 1.4,
  color: "#334155",
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    fontSize: "0.875rem",
    backgroundColor: "#fff",
    borderRadius: "8px",
    color: "#111827",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&.Mui-focused fieldset": {
      borderColor: "#111827",
      borderWidth: "1px",
    },
    "&:hover:not(.Mui-disabled) fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-disabled": {
      backgroundColor: "#f3f4f6",
      "& fieldset": { borderColor: "#e5e7eb" },
    },
    "&.Mui-disabled .MuiOutlinedInput-input, &.Mui-disabled .MuiSelect-select": {
      WebkitTextFillColor: "#64748b",
    },
    "&.Mui-disabled .MuiSelect-icon": {
      color: "#94a3b8",
    },
  },
  "& .MuiOutlinedInput-notchedOutline legend": {
    float: "unset",
    width: "auto",
    maxWidth: "0.01px",
    margin: 0,
    padding: 0,
    fontSize: "0.75em",
    lineHeight: "11px",
    height: "11px",
  },
  "& .MuiOutlinedInput-input::placeholder": {
    color: "#94a3b8",
    opacity: 1,
  },
};

const INPUT_ERROR_SX = {
  "& .MuiOutlinedInput-root.Mui-error fieldset, & .MuiOutlinedInput-root.Mui-error:hover fieldset, & .MuiOutlinedInput-root.Mui-error.Mui-focused fieldset":
    { borderColor: "#dc2626" },
  "& .MuiFormHelperText-root": {
    mx: 0,
    mt: 0.75,
    fontSize: "0.75rem",
    lineHeight: 1.4,
  },
  "& .MuiFormHelperText-root.Mui-error": { color: "#dc2626" },
};

// Chip reflects the last saved Create/Update response, not the local Enable toggle.
const STATUS_CHIP_SX = (active) => ({
  height: 24,
  fontSize: "0.75rem",
  fontWeight: 600,
  borderRadius: "6px",
  backgroundColor: active ? "#ecfdf5" : "#fff7ed",
  color: active ? "#047857" : BRAND_ORANGE,
  border: `1px solid ${active ? "#a7f3d0" : "#fdba74"}`,
  "& .MuiChip-label": { px: 1.25 },
});

const TOGGLE_ROW_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  px: 2,
  py: 1.5,
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  backgroundColor: "#fff",
};

const ENABLED_SWITCH_SX = {
  flexShrink: 0,
  mr: -1,
  "& .MuiSwitch-switchBase.Mui-checked": { color: BRAND_ORANGE },
  "& .MuiSwitch-switchBase.Mui-checked:hover": {
    backgroundColor: "rgba(249, 129, 37, 0.08)",
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: BRAND_ORANGE,
    opacity: 0.5,
  },
};

const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 320,
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
      "& .MuiMenuItem-root": {
        fontSize: "0.875rem",
      },
      "& .MuiMenuItem-root.Mui-selected": {
        backgroundColor: "rgba(249, 129, 37, 0.12)",
      },
      "& .MuiMenuItem-root.Mui-selected:hover": {
        backgroundColor: "rgba(249, 129, 37, 0.18)",
      },
      "& .MuiMenuItem-root:hover": {
        backgroundColor: "#fff4eb",
      },
    },
  },
};

const TWO_COL_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
  gap: 2,
  alignItems: "start",
};

const VALUE_BOX_SX = {
  minHeight: 40,
  display: "flex",
  alignItems: "center",
  minWidth: 0,
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  backgroundColor: "#f3f4f6",
  px: 1.5,
  py: 1,
  cursor: "default",
};

const REQUIRED_MARK_SX = {
  color: "#dc2626",
  ml: 0.25,
};

const PRIMARY_BTN_SX = {
  height: 40,
  minWidth: 112,
  backgroundColor: BRAND_ORANGE,
  color: "#fff",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  boxShadow: "none",
  "&:hover": { backgroundColor: BRAND_ORANGE_HOVER, boxShadow: "none" },
  "&.Mui-disabled": {
    backgroundColor: "#e5e7eb",
    color: "#9ca3af",
  },
};

const COPY_BTN_SX = {
  height: 40,
  minWidth: 92,
  flexShrink: 0,
  px: 1.75,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.8125rem",
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  color: "#111827",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#f8fafc",
    borderColor: "#cbd5e1",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: "#9ca3af",
    borderColor: "#e5e7eb",
    backgroundColor: "#fff",
  },
};

const TEST_BTN_SX = {
  ...COPY_BTN_SX,
  minWidth: 140,
  whiteSpace: "nowrap",
  color: BRAND_ORANGE,
  borderColor: BRAND_ORANGE,
  "&:hover": {
    backgroundColor: "#fff7ed",
    borderColor: BRAND_ORANGE_HOVER,
    color: BRAND_ORANGE_HOVER,
    boxShadow: "none",
  },
};

const ALERT_SX = {
  borderRadius: "8px",
  fontSize: "0.8125rem",
  alignItems: "center",
};

const PROGRESS_SX = {
  borderRadius: "4px",
  backgroundColor: "#ffedd5",
  "& .MuiLinearProgress-bar": { backgroundColor: BRAND_ORANGE },
};

const HELP_TEXT_SX = {
  color: "#64748b",
  fontSize: "0.8125rem",
  lineHeight: 1.5,
};

const DIALOG_CANCEL_BTN_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: 14,
  borderRadius: "8px",
  px: 2.5,
  color: "#475569",
  borderColor: "#cbd5e1",
  "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f8fafc" },
};

const DIALOG_CONFIRM_BTN_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: 14,
  borderRadius: "8px",
  px: 2.5,
  boxShadow: "none",
  backgroundColor: BRAND_ORANGE,
  "&:hover": { backgroundColor: BRAND_ORANGE_HOVER, boxShadow: "none" },
};

const EMPTY_FORM = {
  providerType: "OKTA",
  displayName: "Okta",
  targetEmailDomain: "",
  idpMetadataUrl: "",
  enabled: false,
  configStatus: "DRAFT",
  protocol: "SAML",
  acsUrl: "",
  audienceUri: "",
  cognitoProviderName: "",
};

function readRole() {
  try {
    return getUserRole();
  } catch (error) {
    return "NONE";
  }
}

function isHttpsUrl(value) {
  if (/\s/.test(value)) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" && Boolean(parsed.hostname);
  } catch (error) {
    return false;
  }
}

function formatDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function comparableValue(form, field) {
  const value = form[field];
  return typeof value === "string" ? value.trim() : value;
}

function isSavedConfig(config) {
  return Boolean(config && String(config.idpMetadataUrl || "").trim());
}

function domainFromEmail(email) {
  if (!email || typeof email !== "string") return "";
  const at = email.lastIndexOf("@");
  if (at < 1 || at === email.length - 1) return "";
  return email.slice(at + 1).toLowerCase();
}

function readOrgEmailDomain(organization) {
  return domainFromEmail(
    organization?.orgprimaryemail || organization?.orgbillingemail
  );
}

function formFromConfig(config, fallbackProvider) {
  const provider =
    resolveProvider(config?.providerType) ||
    resolveProvider(fallbackProvider) ||
    resolveProvider("OKTA");
  return {
    providerType: provider.value,
    displayName: config?.displayName || provider.displayName || "",
    targetEmailDomain: config?.targetEmailDomain || "",
    idpMetadataUrl: config?.idpMetadataUrl || "",
    enabled: Boolean(config?.enabled),
    configStatus: config?.status || "DRAFT",
    protocol: config?.protocol || "SAML",
    acsUrl: config?.acsUrl || "",
    audienceUri: config?.audienceUri || "",
    cognitoProviderName: config?.cognitoProviderName || "",
  };
}

export default function SsoConfig() {
  const dispatch = useDispatch();
  const { loading, saving, config, error, saveError, saveMessage } = useSelector(
    (state) => state.ssoConfig
  );
  // Configuration loads `/cloudnova/accounts` into acctList; orgAccount is a second path for the same API.
  const organization = useSelector((state) => {
    const fromAcctList = state.acctList?.organizations;
    const fromOrgAccount = state.orgAccount?.organizations;
    const list =
      Array.isArray(fromAcctList) && fromAcctList.length > 0
        ? fromAcctList
        : Array.isArray(fromOrgAccount) && fromOrgAccount.length > 0
          ? fromOrgAccount
          : null;
    return list ? list[0] : null;
  });
  const role = readRole();
  const canWrite = role === "Administrator";
  const [form, setForm] = useState(EMPTY_FORM);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);
  const [copiedField, setCopiedField] = useState("");
  const [touched, setTouched] = useState({});
  const [confirmTurnOffOpen, setConfirmTurnOffOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const savedForm = isSavedConfig(config) ? formFromConfig(config) : null;
  const lockedProvider = isSavedConfig(config)
    ? resolveProvider(config.providerType)?.value || ""
    : "";
  const lockedProviderLabel =
    resolveProvider(lockedProvider)?.label ||
    resolveProvider(lockedProvider)?.displayName ||
    lockedProvider;
  const providerLabel =
    resolveProvider(form.providerType)?.label ||
    resolveProvider(form.providerType)?.displayName ||
    form.providerType;
  const relayState = config?.idpInitiatedRelayState || "";
  const configSaved = isSavedConfig(config);
  const orgEmailDomain = readOrgEmailDomain(organization);
  const metadataUrl = form.idpMetadataUrl.trim();

  // Backends without Test Connection omit the test fields; enforcing the test there would block enabling SSO.
  const supportsTest = Boolean(
    config && typeof config === "object" && "lastTestStatus" in config
  );
  const currentTest =
    testResult &&
    testResult.url === metadataUrl &&
    testResult.providerType === form.providerType
      ? testResult
      : null;
  const savedPassedTest =
    config?.lastTestStatus === "PASSED" &&
    String(config?.lastTestedMetadataUrl || "").trim() === metadataUrl;
  const liveUrlUnchanged = Boolean(
    savedForm?.enabled && savedForm.idpMetadataUrl.trim() === metadataUrl
  );
  const testRequired =
    supportsTest && !liveUrlUnchanged && !savedPassedTest && !currentTest?.success;

  // Turning on requires a passing test when supported; turning off stays available.
  const enabledSwitchDisabled = !canWrite || (!form.enabled && testRequired);
  // Create/Update success writes the response into `config`; the chip follows that, not the toggle.
  const ssoActive = Boolean(savedForm?.enabled);

  useEffect(() => {
    dispatch(fetchSsoConfig({ providerType: "OKTA" }));
    return () => {
      dispatch(clearSsoConfigMessages());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!config) return;
    if (isSavedConfig(config)) {
      setForm(formFromConfig(config));
      setHasExistingConfig(true);
      return;
    }
    setForm((current) => {
      const next = formFromConfig(config, current.providerType);
      const keepDisplayName =
        current.displayName &&
        !PREFILL_DISPLAY_NAMES.includes(current.displayName);
      const domain =
        current.targetEmailDomain.trim() ||
        readOrgEmailDomain(organization) ||
        next.targetEmailDomain;
      return {
        ...current,
        providerType: next.providerType || current.providerType,
        displayName: keepDisplayName ? current.displayName : next.displayName,
        targetEmailDomain: domain,
        protocol: next.protocol || current.protocol,
        acsUrl: next.acsUrl,
        audienceUri: next.audienceUri,
        cognitoProviderName: next.cognitoProviderName,
      };
    });
  }, [config, organization]);

  useEffect(() => {
    if (!orgEmailDomain) return;
    setForm((current) => {
      if (current.targetEmailDomain.trim()) return current;
      return { ...current, targetEmailDomain: orgEmailDomain };
    });
  }, [orgEmailDomain]);

  useEffect(() => {
    if (saveError?.status === 409) setHasExistingConfig(true);
    if (saveError?.status === 404) setHasExistingConfig(false);
  }, [saveError]);

  useEffect(() => {
    if (!saveMessage) return;
    dispatch(clearSsoConfigMessages());
  }, [saveMessage, dispatch]);

  useEffect(() => {
    if (!copiedField) return undefined;
    const timer = setTimeout(() => setCopiedField(""), 2000);
    return () => clearTimeout(timer);
  }, [copiedField]);

  const setField = (field) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => {
      if (field !== "enabled") return { ...current, [field]: value };
      return {
        ...current,
        enabled: value,
        configStatus: value ? "ACTIVE" : "DISABLED",
      };
    });
  };

  const markTouched = (field) => () => {
    setTouched((current) => (current[field] ? current : { ...current, [field]: true }));
  };

  const handleProviderChange = (event) => {
    if (lockedProvider) return;
    const provider = resolveProvider(event.target.value);
    if (!provider) return;
    setForm((current) => ({
      ...current,
      providerType: provider.value,
      displayName: PREFILL_DISPLAY_NAMES.includes(current.displayName)
        ? provider.displayName
        : current.displayName,
    }));
    setCopiedField("");
    dispatch(clearSsoConfigMessages());
    dispatch(fetchSsoConfig({ providerType: provider.value }));
  };

  const copyValue = async (field, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
    } catch (copyError) {
      setCopiedField("");
    }
  };

  const handleTestConnection = async () => {
    const url = metadataUrl;
    const providerType = form.providerType;
    setTesting(true);
    setTestResult(null);
    try {
      const response = await postSsoConfigTest({
        providerType,
        protocol: "SAML",
        idpMetadataUrl: url,
      });
      const data = response?.data;
      const success = response?.status === 200 && data?.success === true;
      const message =
        (typeof data?.message === "string" && data.message.trim()) ||
        (response?.status !== 200 ? getApiErrorMessage(response) : "") ||
        (success ? "SAML metadata validated successfully." : TEST_FAILED_FALLBACK);
      setTestResult({
        url,
        providerType,
        success,
        message,
        certificateExpiry: success ? formatDate(data?.metadata?.certificateExpiry) : "",
      });
    } catch (testError) {
      setTestResult({ url, providerType, success: false, message: TEST_FAILED_FALLBACK });
    } finally {
      setTesting(false);
    }
  };

  const submitSave = () => {
    setConfirmTurnOffOpen(false);
    dispatch(clearSsoConfigMessages());
    dispatch(
      saveSsoConfig({
        method: hasExistingConfig ? "PUT" : "POST",
        body: {
          enabled: form.enabled,
          providerType: form.providerType,
          displayName: form.displayName.trim(),
          protocol: "SAML",
          targetEmailDomain: form.targetEmailDomain.trim(),
          idpMetadataUrl: form.idpMetadataUrl.trim(),
          status: form.enabled ? "ACTIVE" : "DISABLED",
        },
      })
    );
  };

  const isLiveSaved = Boolean(savedForm?.enabled);
  const willBeLive = form.enabled;

  const handleSave = () => {
    if (isLiveSaved && !willBeLive) {
      setConfirmTurnOffOpen(true);
      return;
    }
    submitSave();
  };

  const isDirty =
    !savedForm ||
    EDITABLE_FIELDS.some(
      (field) => comparableValue(form, field) !== comparableValue(savedForm, field)
    );

  const displayNameError = /\s/.test(form.displayName)
    ? "Display name cannot contain spaces."
    : "";

  const emailDomain = form.targetEmailDomain.trim();
  const emailDomainError =
    emailDomain && !DOMAIN_PATTERN.test(emailDomain)
      ? "Enter a domain like company.com, without @ or spaces."
      : "";

  const metadataUrlError =
    metadataUrl && !isHttpsUrl(metadataUrl)
      ? "Enter a valid URL that starts with https://."
      : "";

  const saveBlockedReason = () => {
    if (!form.displayName.trim()) return "Display name is required.";
    if (displayNameError) return displayNameError;
    if (!emailDomain) return "Email domain is required.";
    if (emailDomainError) return emailDomainError;
    if (!metadataUrl) return "SAML metadata URL is required.";
    if (metadataUrlError) return metadataUrlError;
    if (form.enabled && testRequired) return TEST_REQUIRED_MESSAGE;
    return "";
  };

  const blockedReason = saveBlockedReason();

  const showAlerts = Boolean(error || saveError?.message || !canWrite);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        p: { xs: 1.5, sm: 2.5 },
        backgroundColor: "#f3f4f6",
        boxSizing: "border-box",
        ...ACCOUNT_DETAIL_FONT_SX,
      }}
    >
      <Box
        sx={{
          pb: 1.25,
          mb: 1.75,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "1rem" }}>
          Single Sign-On
        </Typography>
      </Box>

      <Typography
        sx={{
          color: "#64748b",
          fontSize: "0.875rem",
          fontWeight: 400,
          lineHeight: 1.5,
          mb: 2,
        }}
      >
        Copy the CloudCatcher values into the identity provider, then save the provider metadata URL.
      </Typography>

      {(loading || saving) && <LinearProgress sx={{ ...PROGRESS_SX, mb: 2 }} />}

      {showAlerts && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
          {error && <Alert severity="error" sx={ALERT_SX}>{error}</Alert>}
          {saveError?.message && (
            <Alert severity="error" sx={ALERT_SX}>{saveError.message}</Alert>
          )}
          {!canWrite && (
            <Alert severity="info" sx={ALERT_SX}>
              Only an Administrator can save Single Sign-On configuration.
            </Alert>
          )}
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionCard title="CloudCatcher Values">
          <CopyField
            label="Single Sign-On URL"
            value={form.acsUrl}
            copied={copiedField === "acsUrl"}
            onCopy={() => copyValue("acsUrl", form.acsUrl)}
          />
          <CopyField
            label="Audience URI / SP Entity ID"
            value={form.audienceUri}
            copied={copiedField === "audienceUri"}
            onCopy={() => copyValue("audienceUri", form.audienceUri)}
          />
          {relayState && (
            <CopyField
              label="Default RelayState"
              value={relayState}
              copied={copiedField === "relayState"}
              onCopy={() => copyValue("relayState", relayState)}
            />
          )}
          <ReadOnlyValue label="Protocol" value={form.protocol} />
        </SectionCard>

        <SectionCard
          title="Identity Provider"
          action={
            <Chip
              label={ssoActive ? "Active" : "Inactive"}
              size="small"
              sx={STATUS_CHIP_SX(ssoActive)}
            />
          }
        >
          <Box sx={TWO_COL_SX}>
            <Field label="Provider" htmlFor="sso-provider">
              <Tooltip
                title={
                  lockedProvider
                    ? `This organization already uses ${lockedProviderLabel}. Only one identity provider can be configured.`
                    : ""
                }
                placement="top"
                arrow
              >
                <Box component="span" sx={{ display: "block" }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    id="sso-provider"
                    value={form.providerType}
                    onChange={handleProviderChange}
                    disabled={loading || Boolean(lockedProvider)}
                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                    sx={INPUT_SX}
                  >
                    {PROVIDERS.map((provider) => (
                      <MenuItem key={provider.value} value={provider.value}>
                        {provider.label || provider.displayName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Tooltip>
            </Field>
            <Field label="Display name" htmlFor="sso-display-name" required>
              <TextField
                fullWidth
                size="small"
                id="sso-display-name"
                value={form.displayName}
                onChange={setField("displayName")}
                disabled={!canWrite}
                required
                error={Boolean(displayNameError)}
                helperText={displayNameError || undefined}
                sx={{ ...INPUT_SX, ...INPUT_ERROR_SX }}
              />
            </Field>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md:
                    canWrite && supportsTest
                      ? "minmax(140px, 180px) minmax(0, 1fr) auto"
                      : "minmax(140px, 180px) minmax(0, 1fr)",
                },
                gap: 2,
                alignItems: "start",
                minWidth: 0,
              }}
            >
              <Field label="Email domain" htmlFor="sso-email-domain" required>
                <TextField
                  fullWidth
                  size="small"
                  id="sso-email-domain"
                  value={form.targetEmailDomain}
                  onChange={setField("targetEmailDomain")}
                  onBlur={markTouched("targetEmailDomain")}
                  placeholder="customer.com"
                  disabled={!canWrite}
                  required
                  error={Boolean(touched.targetEmailDomain && emailDomainError)}
                  helperText={(touched.targetEmailDomain && emailDomainError) || undefined}
                  sx={{ ...INPUT_SX, ...INPUT_ERROR_SX }}
                />
              </Field>
              <Field label="SAML metadata URL" htmlFor="sso-metadata-url" required>
                <TextField
                  fullWidth
                  size="small"
                  id="sso-metadata-url"
                  value={form.idpMetadataUrl}
                  onChange={setField("idpMetadataUrl")}
                  onBlur={markTouched("idpMetadataUrl")}
                  disabled={!canWrite}
                  required
                  error={Boolean(touched.idpMetadataUrl && metadataUrlError)}
                  helperText={(touched.idpMetadataUrl && metadataUrlError) || undefined}
                  sx={{ ...INPUT_SX, ...INPUT_ERROR_SX }}
                  inputProps={{
                    spellCheck: "false",
                    title: form.idpMetadataUrl || undefined,
                  }}
                />
              </Field>
              {canWrite && supportsTest ? (
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    component="div"
                    aria-hidden="true"
                    sx={{ ...FIELD_LABEL_SX, visibility: "hidden", userSelect: "none" }}
                  >
                    Test
                  </Typography>
                  <Button
                    onClick={handleTestConnection}
                    disabled={testing || loading || !metadataUrl || Boolean(metadataUrlError)}
                    sx={TEST_BTN_SX}
                  >
                    {testing ? "Testing..." : "Test Connection"}
                  </Button>
                </Box>
              ) : null}
            </Box>
            {canWrite && supportsTest && (
              <TestConnectionStatus
                result={currentTest}
                lastPassedOn={
                  !currentTest && savedPassedTest ? formatDate(config?.lastTestedAt) : ""
                }
              />
            )}
          </Box>

          <Box sx={TOGGLE_ROW_SX}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="label"
                htmlFor="sso-enabled"
                sx={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  lineHeight: 1.4,
                  color: enabledSwitchDisabled ? "#94a3b8" : "#111827",
                  cursor: enabledSwitchDisabled ? "default" : "pointer",
                }}
              >
                Enable Single Sign-On
              </Typography>
              <Typography
                id="sso-enabled-description"
                sx={{ fontSize: "0.8125rem", lineHeight: 1.5, color: "#64748b", mt: 0.25 }}
              >
                Users set to Single Sign-On sign in through {providerLabel} instead of a password.
                {!form.enabled &&
                  testRequired &&
                  " Run Test Connection on this metadata URL to turn this on."}
              </Typography>
            </Box>
            <Switch
              id="sso-enabled"
              checked={form.enabled}
              onChange={setField("enabled")}
              disabled={enabledSwitchDisabled}
              inputProps={{ "aria-describedby": "sso-enabled-description" }}
              sx={ENABLED_SWITCH_SX}
            />
          </Box>

          {canWrite && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <Button
                onClick={handleSave}
                disabled={saving || loading || Boolean(blockedReason) || !isDirty}
                sx={PRIMARY_BTN_SX}
              >
                {saving ? "Saving..." : hasExistingConfig ? "Update" : "Create"}
              </Button>
              {blockedReason && (
                <Typography
                  sx={{ color: "#64748b", fontSize: "0.8125rem", mt: 1, textAlign: "right" }}
                >
                  {blockedReason}
                </Typography>
              )}
            </Box>
          )}
        </SectionCard>

        {config &&
          (configSaved ? (
            <ScimProvisioning canWrite={canWrite} />
          ) : (
            <SectionCard title="SCIM Provisioning">
              <Typography sx={HELP_TEXT_SX}>{SCIM_SAVE_FIRST_MESSAGE}</Typography>
            </SectionCard>
          ))}
      </Box>

      <ConfirmTurnOffDialog
        open={confirmTurnOffOpen}
        onCancel={() => setConfirmTurnOffOpen(false)}
        onConfirm={submitSave}
      />
    </Box>
  );
}

function ConfirmTurnOffDialog({ open, onCancel, onConfirm }) {
  return (
    <ConfirmDialog
      open={open}
      title="Turn Off Single Sign-On"
      message="Are you sure you want to turn off Single Sign-On for this organization?"
      detail="Users set to Single Sign-On will no longer be offered Single Sign-On when they sign in. Anyone already signed in may stay signed in until they sign out or their session expires."
      confirmLabel="Turn Off"
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}

function ConfirmDialog({ open, title, message, detail, confirmLabel, onCancel, onConfirm }) {
  return (
    <DialogShell open={open} title={title} onClose={onCancel}>
      <Typography sx={{ fontSize: "0.9375rem", color: "#334155", lineHeight: 1.5, mb: 1 }}>
        {message}
      </Typography>
      <Typography sx={HELP_TEXT_SX}>{detail}</Typography>
      <DialogActionsRow>
        <Button variant="outlined" onClick={onCancel} sx={DIALOG_CANCEL_BTN_SX}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={onConfirm} sx={DIALOG_CONFIRM_BTN_SX}>
          {confirmLabel}
        </Button>
      </DialogActionsRow>
    </DialogShell>
  );
}

function DialogActionsRow({ children }) {
  return (
    <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 1.25 }}>
      {children}
    </Box>
  );
}

function DialogShell({ open, title, onClose, maxWidth = "xs", children }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      sx={{ zIndex: 1400 }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          width: { xs: "calc(100% - 24px)", sm: "100%" },
          m: { xs: 1.5, sm: 2 },
          overflow: "hidden",
          ...ACCOUNT_DETAIL_FONT_SX,
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          backgroundColor: BRAND_ORANGE,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.15rem",
            color: "#FFFFFF",
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={() => onClose()}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: "#FFFFFF",
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <span aria-hidden="true">×</span>
        </Box>
      </Box>
      <DialogContent sx={{ px: 3, py: 2.5 }}>{children}</DialogContent>
    </Dialog>
  );
}

function ScimProvisioning({ canWrite }) {
  const [scim, setScim] = useState(null);
  const [loadingScim, setLoadingScim] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [pendingAction, setPendingAction] = useState("");
  const [actionError, setActionError] = useState("");
  const [confirmRotateOpen, setConfirmRotateOpen] = useState(false);
  const [confirmRevokeOpen, setConfirmRevokeOpen] = useState(false);
  // The one-time token lives only in this component's state, never in Redux or browser storage.
  const [issuedToken, setIssuedToken] = useState(null);
  const [baseUrlCopied, setBaseUrlCopied] = useState(false);

  const loadScimConfig = useCallback(async () => {
    setLoadingScim(true);
    setLoadError("");
    try {
      const response = await getScimConfig();
      if (response?.status === 200 && response.data && typeof response.data === "object") {
        setScim(response.data);
      } else {
        setLoadError(getApiErrorMessage(response));
      }
    } catch (error) {
      setLoadError(SCIM_LOAD_FALLBACK);
    } finally {
      setLoadingScim(false);
    }
  }, []);

  useEffect(() => {
    loadScimConfig();
  }, [loadScimConfig]);

  useEffect(() => {
    if (!baseUrlCopied) return undefined;
    const timer = setTimeout(() => setBaseUrlCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [baseUrlCopied]);

  const tokenConfigured = scim?.tokenConfigured === true;
  const baseUrl = typeof scim?.baseUrl === "string" ? scim.baseUrl : "";
  const busy = Boolean(pendingAction) || loadingScim;

  const copyBaseUrl = async () => {
    if (!baseUrl) return;
    try {
      await navigator.clipboard.writeText(baseUrl);
      setBaseUrlCopied(true);
    } catch (copyError) {
      setBaseUrlCopied(false);
    }
  };

  const issueToken = async (action) => {
    setConfirmRotateOpen(false);
    setPendingAction(action);
    setActionError("");
    try {
      const response = await postScimToken();
      if (response?.status !== 201) {
        setActionError(getApiErrorMessage(response));
        return;
      }
      const token = response.data?.token;
      const message = response.data?.message;
      if (typeof token === "string" && token) {
        setIssuedToken({
          value: token,
          message:
            (typeof message === "string" && message.trim()) || SCIM_TOKEN_FALLBACK_MESSAGE,
        });
      } else {
        setActionError(SCIM_ACTION_FALLBACK);
      }
      await loadScimConfig();
    } catch (error) {
      setActionError(SCIM_ACTION_FALLBACK);
    } finally {
      setPendingAction("");
    }
  };

  const revokeToken = async () => {
    setConfirmRevokeOpen(false);
    setPendingAction("revoke");
    setActionError("");
    try {
      const response = await deleteScimToken();
      const alreadyRevoked =
        response?.status === 404 && getApiErrorMessage(response) === SCIM_NOT_CONFIGURED;
      if (response?.status !== 204 && !alreadyRevoked) {
        setActionError(getApiErrorMessage(response));
        return;
      }
      if (response.status === 204) {
        enqueueSnackbar("SCIM token revoked.", { variant: "success" });
      }
      await loadScimConfig();
    } catch (error) {
      setActionError(SCIM_ACTION_FALLBACK);
    } finally {
      setPendingAction("");
    }
  };

  return (
    <>
      <SectionCard title="SCIM Provisioning">
        <Typography sx={HELP_TEXT_SX}>
          Add the SCIM Connector Base URL and token to the provisioning settings of the Okta app.
        </Typography>

        {loadingScim && <LinearProgress sx={PROGRESS_SX} />}

        {loadError && (
          <Alert
            severity="error"
            sx={ALERT_SX}
            action={
              <Button
                size="small"
                onClick={loadScimConfig}
                disabled={loadingScim}
                sx={{ textTransform: "none", fontWeight: 600, color: "inherit" }}
              >
                Retry
              </Button>
            }
          >
            {loadError}
          </Alert>
        )}

        {scim && (
          <>
            <CopyField
              label="SCIM Connector Base URL"
              value={baseUrl}
              copied={baseUrlCopied}
              onCopy={copyBaseUrl}
            />
            <Box sx={TWO_COL_SX}>
              <ReadOnlyValue label="Authorization" value={scim.authorizationScheme || ""} />
              <ReadOnlyValue
                label="Token status"
                value={tokenConfigured ? "Configured" : "Not configured"}
              />
              <ReadOnlyValue label="Created" value={formatDate(scim.createdAt)} />
              <ReadOnlyValue label="Last rotated" value={formatDate(scim.lastRotatedAt)} />
            </Box>
          </>
        )}

        {actionError && (
          <Alert severity="error" sx={ALERT_SX}>
            {actionError}
          </Alert>
        )}

        {canWrite && scim && (
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 1.25 }}
          >
            {tokenConfigured ? (
              <>
                <Button
                  onClick={() => setConfirmRevokeOpen(true)}
                  disabled={busy}
                  sx={{ ...COPY_BTN_SX, minWidth: 112 }}
                >
                  {pendingAction === "revoke" ? "Revoking..." : "Revoke token"}
                </Button>
                <Button
                  onClick={() => setConfirmRotateOpen(true)}
                  disabled={busy}
                  sx={PRIMARY_BTN_SX}
                >
                  {pendingAction === "rotate" ? "Rotating..." : "Rotate token"}
                </Button>
              </>
            ) : (
              <Button onClick={() => issueToken("generate")} disabled={busy} sx={PRIMARY_BTN_SX}>
                {pendingAction === "generate" ? "Generating..." : "Generate token"}
              </Button>
            )}
          </Box>
        )}
      </SectionCard>

      <ConfirmDialog
        open={confirmRotateOpen}
        title="Rotate SCIM Token"
        message="Are you sure you want to rotate the SCIM token?"
        detail="The current token stops working immediately. Okta cannot provision users until the new token is added to the Okta app."
        confirmLabel="Rotate"
        onCancel={() => setConfirmRotateOpen(false)}
        onConfirm={() => issueToken("rotate")}
      />
      <ConfirmDialog
        open={confirmRevokeOpen}
        title="Revoke SCIM Token"
        message="Are you sure you want to revoke the SCIM token?"
        detail="The current token stops working immediately. Okta cannot provision users until a new token is generated and added to the Okta app."
        confirmLabel="Revoke"
        onCancel={() => setConfirmRevokeOpen(false)}
        onConfirm={revokeToken}
      />
      <ScimTokenDialog
        token={issuedToken?.value || ""}
        message={issuedToken?.message || ""}
        onClose={() => setIssuedToken(null)}
      />
    </>
  );
}

function ScimTokenDialog({ token, message, onClose }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copyToken = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
    } catch (copyError) {
      setCopied(false);
    }
  };

  // Closing discards the token for good, so a stray backdrop click must not close the dialog.
  const handleClose = (event, reason) => {
    if (reason === "backdropClick") return;
    setCopied(false);
    onClose();
  };

  return (
    <DialogShell open={Boolean(token)} title="SCIM Token" onClose={handleClose} maxWidth="sm">
      <Typography sx={{ fontSize: "0.9375rem", color: "#334155", lineHeight: 1.5, mb: 2 }}>
        {message}
      </Typography>
      <CopyField label="Token" value={token} copied={copied} onCopy={copyToken} />
      <DialogActionsRow>
        <Button
          variant="contained"
          disableElevation
          onClick={handleClose}
          sx={DIALOG_CONFIRM_BTN_SX}
        >
          Done
        </Button>
      </DialogActionsRow>
    </DialogShell>
  );
}

function SectionCard({ title, action, children }) {
  return (
    <Box
      sx={{
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        backgroundColor: "#fff",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.5,
          borderBottom: "1px solid #f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          minWidth: 0,
        }}
      >
        <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: "0.875rem" }}>
          {title}
        </Typography>
        {action}
      </Box>
      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minWidth: 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function Field({ label, htmlFor, required = false, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="label" htmlFor={htmlFor} sx={FIELD_LABEL_SX}>
        {label}
        {required && (
          <Box component="span" aria-hidden="true" sx={REQUIRED_MARK_SX}>
            *
          </Box>
        )}
      </Typography>
      {children}
    </Box>
  );
}

function ReadOnlyValue({ label, value }) {
  const hasValue = Boolean(value);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="div" sx={FIELD_LABEL_SX}>
        {label}
      </Typography>
      <Box sx={VALUE_BOX_SX}>
        <Typography
          component="div"
          sx={{
            width: "100%",
            fontSize: "0.8125rem",
            lineHeight: 1.5,
            color: hasValue ? "#64748b" : "#94a3b8",
            wordBreak: "break-all",
          }}
        >
          {hasValue ? value : "—"}
        </Typography>
      </Box>
    </Box>
  );
}

function TestConnectionStatus({ result, lastPassedOn }) {
  if (!result && !lastPassedOn) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
      {lastPassedOn && (
        <Typography sx={HELP_TEXT_SX}>
          This URL passed Test Connection on {lastPassedOn}.
        </Typography>
      )}
      {result && (
        <Alert severity={result.success ? "success" : "error"} sx={ALERT_SX} role="status">
          {result.message}
          {result.success && result.certificateExpiry &&
            ` Signing certificate expires ${result.certificateExpiry}.`}
        </Alert>
      )}
    </Box>
  );
}

function CopyField({ label, value, copied, onCopy }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="div" sx={FIELD_LABEL_SX}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", minWidth: 0 }}>
        <Box sx={{ ...VALUE_BOX_SX, flex: 1 }}>
          <Typography
            component="div"
            sx={{
              width: "100%",
              fontSize: "0.8125rem",
              lineHeight: 1.5,
              color: value ? "#64748b" : "#94a3b8",
              wordBreak: "break-all",
              userSelect: "all",
            }}
          >
            {value || "—"}
          </Typography>
        </Box>
        <Button
          onClick={onCopy}
          disabled={!value}
          aria-label={copied ? `${label} copied` : `Copy ${label}`}
          sx={{
            ...COPY_BTN_SX,
            ...(copied
              ? {
                  color: BRAND_ORANGE,
                  borderColor: "#fdba74",
                  backgroundColor: "#fff7ed",
                  "&:hover": {
                    backgroundColor: "#fff7ed",
                    borderColor: "#fdba74",
                    boxShadow: "none",
                  },
                }
              : {}),
          }}
        >
          {copied ? "Copied" : "Copy"}
        </Button>
      </Box>
    </Box>
  );
}
